import React, { useState, useRef, useEffect } from 'react';
import { Box, Dialog, DialogContent, DialogActions, Button, Typography } from '@mui/material';

interface MonthYearPickerProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (month: number, year: number) => void;
  initialMonth?: number;
  initialYear?: number;
}

const MONTHS = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
];

const YEARS = [2025, 2026, 2027];
const ITEM_HEIGHT = 50;
const VISIBLE_ITEMS = 5;
const PADDING_ITEMS = 2;

const MonthYearPicker: React.FC<MonthYearPickerProps> = ({
  open,
  onClose,
  onConfirm,
  initialMonth = new Date().getMonth(),
  initialYear = new Date().getFullYear()
}) => {
  const [selectedMonth, setSelectedMonth] = useState(initialMonth);
  const [selectedYear, setSelectedYear] = useState(initialYear);
  
  const monthScrollRef = useRef<HTMLDivElement>(null);
  const yearScrollRef = useRef<HTMLDivElement>(null);
  
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isScrollingRef = useRef(false);

  // Crea array infinito di mesi per scroll continuo
  const createInfiniteMonths = () => {
    const result: { value: number; label: string; key: string }[] = [];
    for (let i = 0; i < 100; i++) {
      MONTHS.forEach((month, idx) => {
        result.push({
          value: idx,
          label: month,
          key: `${i}-${idx}`
        });
      });
    }
    return result;
  };

  const infiniteMonths = createInfiniteMonths();
  const centerIndex = Math.floor(infiniteMonths.length / 2);

  // Inizializza la posizione al centro quando si apre il dialog
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        if (monthScrollRef.current) {
          const targetIdx = centerIndex - (centerIndex % 12) + selectedMonth;
          monthScrollRef.current.scrollTop = (targetIdx - PADDING_ITEMS) * ITEM_HEIGHT;
        }
        
        if (yearScrollRef.current) {
          const yearIdx = YEARS.indexOf(selectedYear);
          if (yearIdx !== -1) {
            yearScrollRef.current.scrollTop = yearIdx * ITEM_HEIGHT;
          }
        }
      }, 50);
    }
  }, [open, centerIndex, selectedMonth, selectedYear]);

  // Calcola il valore selezionato dalla posizione di scroll
  const updateSelectedFromScroll = () => {
    // Mesi
    if (monthScrollRef.current) {
      const scrollTop = monthScrollRef.current.scrollTop;
      const centerIdx = Math.round(scrollTop / ITEM_HEIGHT) + PADDING_ITEMS;
      if (infiniteMonths[centerIdx]) {
        setSelectedMonth(infiniteMonths[centerIdx].value);
      }
    }
    
    // Anni - considera il padding
    if (yearScrollRef.current) {
      const scrollTop = yearScrollRef.current.scrollTop;
      // scrollTop / ITEM_HEIGHT ci dà il primo elemento scrollato
      // Aggiungendo PADDING_ITEMS otteniamo l'elemento al centro visivo
      const visualIdx = Math.round(scrollTop / ITEM_HEIGHT) + PADDING_ITEMS;
      // Sottraiamo il padding per ottenere l'indice reale dell'array YEARS
      const yearIdx = visualIdx - PADDING_ITEMS;
      
      if (yearIdx >= 0 && yearIdx < YEARS.length) {
        setSelectedYear(YEARS[yearIdx]);
      }
    }
  };

  // Handler per lo scroll con debouncing
  const handleScroll = () => {
    isScrollingRef.current = true;
    
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    scrollTimeoutRef.current = setTimeout(() => {
      updateSelectedFromScroll();
      isScrollingRef.current = false;
    }, 150);
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  const handleConfirm = () => {
    onConfirm(selectedMonth, selectedYear);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden'
        }
      }}
    >
      <DialogContent sx={{ py: 3, px: 2 }}>
        <Typography variant="h6" sx={{ textAlign: 'center', mb: 3, fontWeight: 600 }}>
          Seleziona Mese e Anno
        </Typography>
        
        <Box sx={{ 
          display: 'flex', 
          gap: 1,
          justifyContent: 'center',
          height: ITEM_HEIGHT * VISIBLE_ITEMS,
          position: 'relative'
        }}>
          {/* Overlay superiore */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: ITEM_HEIGHT * 2,
            background: 'linear-gradient(to bottom, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.7) 70%, transparent 100%)',
            pointerEvents: 'none',
            zIndex: 2
          }} />
          
          {/* Overlay inferiore */}
          <Box sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: ITEM_HEIGHT * 2,
            background: 'linear-gradient(to top, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.7) 70%, transparent 100%)',
            pointerEvents: 'none',
            zIndex: 2
          }} />

          {/* Indicatore di selezione */}
          <Box sx={{
            position: 'absolute',
            top: ITEM_HEIGHT * 2,
            left: 0,
            right: 0,
            height: ITEM_HEIGHT,
            borderTop: '2px solid',
            borderBottom: '2px solid',
            borderColor: 'primary.main',
            backgroundColor: 'rgba(33, 150, 243, 0.08)',
            pointerEvents: 'none',
            zIndex: 1,
            borderRadius: 1
          }} />

          {/* Colonna Mese */}
          <Box sx={{ flex: 1, position: 'relative' }}>
            <Box
              ref={monthScrollRef}
              onScroll={handleScroll}
              sx={{
                height: '100%',
                overflowY: 'scroll',
                scrollSnapType: 'y mandatory',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              {infiniteMonths.map((month, idx) => {
                const isCentered = !isScrollingRef.current && month.value === selectedMonth;
                
                return (
                  <Box
                    key={month.key}
                    sx={{
                      height: ITEM_HEIGHT,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      scrollSnapAlign: 'start',
                      scrollSnapStop: 'always',
                      fontSize: '18px',
                      fontWeight: isCentered ? 600 : 400,
                      color: isCentered ? 'primary.main' : 'text.secondary',
                      transition: 'all 0.15s',
                      cursor: 'pointer',
                      userSelect: 'none'
                    }}
                    onClick={() => {
                      if (monthScrollRef.current) {
                        monthScrollRef.current.scrollTo({
                          top: (idx - PADDING_ITEMS) * ITEM_HEIGHT,
                          behavior: 'smooth'
                        });
                        setSelectedMonth(month.value);
                      }
                    }}
                  >
                    {month.label}
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Colonna Anno */}
          <Box sx={{ flex: 1, position: 'relative' }}>
            <Box
              ref={yearScrollRef}
              onScroll={handleScroll}
              sx={{
                height: '100%',
                overflowY: 'scroll',
                scrollSnapType: 'y mandatory',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              {/* Padding items sopra per centrare */}
              {Array(PADDING_ITEMS).fill(null).map((_, idx) => (
                <Box
                  key={`padding-top-${idx}`}
                  sx={{
                    height: ITEM_HEIGHT,
                    scrollSnapAlign: 'start',
                  }}
                />
              ))}
              
              {/* Anni */}
              {YEARS.map((year, index) => {
                const isCentered = !isScrollingRef.current && year === selectedYear;
                
                return (
                  <Box
                    key={year}
                    sx={{
                      height: ITEM_HEIGHT,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      scrollSnapAlign: 'start',
                      scrollSnapStop: 'always',
                      fontSize: '18px',
                      fontWeight: isCentered ? 600 : 400,
                      color: isCentered ? 'primary.main' : 'text.secondary',
                      transition: 'all 0.15s',
                      cursor: 'pointer',
                      userSelect: 'none'
                    }}
                    onClick={() => {
                      if (yearScrollRef.current) {
                        yearScrollRef.current.scrollTo({
                          top: index * ITEM_HEIGHT,
                          behavior: 'smooth'
                        });
                        setSelectedYear(year);
                      }
                    }}
                  >
                    {year}
                  </Box>
                );
              })}
              
              {/* Padding items sotto per centrare */}
              {Array(PADDING_ITEMS).fill(null).map((_, idx) => (
                <Box
                  key={`padding-bottom-${idx}`}
                  sx={{
                    height: ITEM_HEIGHT,
                    scrollSnapAlign: 'start',
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          Annulla
        </Button>
        <Button onClick={handleConfirm} variant="contained" color="primary">
          Conferma
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MonthYearPicker;
